import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Bell, X, Check } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { alertsApi } from "@/lib/alertsApi";
import { getAlerts } from "@/lib/alertHistory";

/** Server-backed alert history — an alert fired for one admin shows up for every admin.
 *  Falls back to localStorage (`bni-alert-history`) if the API is offline. */
export function AlertHistoryButton() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await alertsApi.list(40);
      setItems(r.items || []);
      setUnseen(r.unseen || 0);
    } catch {
      // fallback: local
      setItems(getAlerts());
      setUnseen(0);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    const onEmit = () => load();
    window.addEventListener("bni-alert-push", onEmit);
    return () => { clearInterval(iv); window.removeEventListener("bni-alert-push", onEmit); };
  }, [load]);

  const markAll = async () => { try { await alertsApi.seenAll(); await load(); } catch {} };

  return (
    <>
      <button
        data-testid="alert-history-btn"
        onClick={() => setOpen(true)}
        className="btn-ghost !py-2 relative"
        title="Alert history"
      >
        <Bell size={14} /> Alerts
        {unseen > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-[10px]"
            style={{ background: "var(--red)", color: "#fff", borderRadius: "50%" }}>{Math.min(unseen, 9)}</span>
        )}
      </button>
      {open && <AlertModal items={items} onClose={() => { setOpen(false); markAll(); }} onMarkAll={markAll} />}
    </>
  );
}

function AlertModal({ items, onClose, onMarkAll }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 bg-black/70 flex items-start justify-end p-4">
      <Panel className="w-full max-w-md p-0 relative max-h-[86vh] flex flex-col" data-testid="alert-history-modal">
        <div className="p-5 hairline-b flex items-center gap-3">
          <MonoLabel>Team Alerts · last {items.length}</MonoLabel>
          <button onClick={onMarkAll} disabled={!items.length} className="btn-ghost !py-1 ml-auto" data-testid="alert-history-seen-all">
            <Check size={12} /> Mark seen
          </button>
          <button onClick={onClose} className="p-1 hover:text-[var(--red)]" data-testid="alert-history-close"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto">
          {items.length === 0 && (
            <div className="p-8 text-center mono-label" style={{ color: "var(--taupe)" }}>
              No alerts on record — team-wide history is quiet right now.
            </div>
          )}
          {items.map((a) => (
            <div key={a.id} className="p-4 hairline-b" data-testid="alert-history-row"
              style={{ background: (a.seen_by || []).length ? "transparent" : "rgba(204,37,47,0.04)" }}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="chip" style={{ color: a.severity === "high" ? "var(--red)" : "var(--red-3)", borderColor: a.severity === "high" ? "var(--red)" : "var(--red-3)" }}>{(a.type || "alert").toUpperCase()}</span>
                <span className="mono-label" style={{ color: "var(--taupe)" }}>{fmtTs(a.created_at || a.ts)}</span>
                {a.emitted_by_phone && <span className="mono-label ml-auto" style={{ color: "var(--muted)" }}>from {a.emitted_by_phone.slice(-4)}</span>}
              </div>
              <div className="text-sm mt-2">{a.title || a.message || "—"}</div>
              {a.detail && <div className="mono-label mt-1" style={{ color: "var(--muted)" }}>{a.detail}</div>}
            </div>
          ))}
        </div>
      </Panel>
    </motion.div>
  );
}

function fmtTs(ts) {
  const t = typeof ts === "string" ? Date.parse(ts) : ts;
  const now = Date.now();
  const d = (now - t) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d/60)}m ago`;
  if (d < 86400) return `${Math.floor(d/3600)}h ago`;
  return new Date(t).toLocaleString();
}
